import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import type { InputType, SessionSummary } from "@re/shared";
import { Badge, Card, Icon, Input, SectionHeading, SourceBadge, VisibilityPill, fmtRelative } from "../components/ui";

export const Route = createFileRoute("/sessions/")({
  component: SessionsList,
});

const ALL_TYPES: InputType[] = ["youtube", "arxiv", "github", "huggingface", "blog", "community", "topic"];

function SessionsList() {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<InputType | "all">("all");
  const list = useQuery({ queryKey: ["sessions", "public"], queryFn: () => api.listPublicSessions() });

  const filtered = useMemo(() => {
    if (!list.data) return [];
    return list.data.filter((s) => {
      if (filter !== "all" && s.input_type !== filter) return false;
      if (!q.trim()) return true;
      const needle = q.toLowerCase();
      return s.title.toLowerCase().includes(needle) || s.input.toLowerCase().includes(needle);
    });
  }, [list.data, q, filter]);

  return (
    <main className="max-w-[1320px] mx-auto px-6 py-10">
      <SectionHeading
        eyebrow={t("landing.feedEyebrow")}
        title={t("landing.feedTitle")}
        action={
          <span className="text-ink-faint font-mono text-[13px]">({list.data?.length ?? "—"})</span>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex-1 min-w-[260px] max-w-[440px]">
          <Input
            icon="search"
            size="md"
            placeholder={t("common.search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as InputType | "all")}
          className="h-9 px-3 bg-surface border border-border rounded-md text-[13px] text-ink-dim"
        >
          <option value="all">{t("me.allSources")}</option>
          {ALL_TYPES.map((tp) => (
            <option key={tp} value={tp}>
              {t(`source.${tp}`)}
            </option>
          ))}
        </select>
      </div>

      {list.isLoading ? (
        <p className="text-ink-faint">{t("common.loading")}</p>
      ) : list.error ? (
        <Card className="p-5 text-[13px] text-[color:var(--danger)]">
          {t("landing.feedLoadFailed", { error: String(list.error) })}
        </Card>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-ink-faint">
          <Icon name="search" size={24} className="mx-auto mb-3 opacity-50" />
          {t("me.emptyFiltered")}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((s) => (
            <FeedCard key={s.slug} s={s} lang={i18n.language} />
          ))}
        </div>
      )}
    </main>
  );
}

/* Local feed card — intentionally duplicated from the landing page (do not import across routes). */
function FeedCard({ s, lang }: { s: SessionSummary; lang: string }) {
  const { t } = useTranslation();
  return (
    <Card interactive as={Link} to="/sessions/$slug" params={{ slug: s.slug }} className="block p-5 group">
      <div className="flex items-start gap-3 mb-3">
        <SourceBadge type={s.input_type} />
        {s.model && (
          <Badge tone="line" size="sm">
            {s.model}
          </Badge>
        )}
        <div className="flex-1" />
        <div className="text-[12px] text-ink-faint font-mono">{fmtRelative(s.created, lang)}</div>
      </div>
      <h3 className="font-serif text-[20px] leading-[1.25] font-medium text-ink tracking-tight mb-2 group-hover:text-accent-ink transition-colors">
        {s.title}
      </h3>
      {s.intent_purpose ? (
        <p className="text-[14px] leading-[1.55] text-ink-dim mb-4 line-clamp-2">{s.intent_purpose}</p>
      ) : (
        <p className="text-[13px] leading-[1.55] text-ink-faint font-mono mb-4 truncate">{s.input}</p>
      )}
      <div className="flex items-center gap-3 pt-3 border-t border-border text-[12px] text-ink-faint font-mono">
        <span>{t("landing.sourceCount", { count: s.source_count })}</span>
        <div className="flex-1" />
        {s.visibility && <VisibilityPill visibility={s.visibility} size="xs" />}
      </div>
    </Card>
  );
}
